/**
 * MapSearch — unified search for places and eSanza entities.
 */

import React, { useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMapSearch } from '../hooks/useMapSearch';
import type { GeoCoord } from '../types/mapTypes';

interface MapSearchProps {
  onSelect?: (result: any) => void;
  proximity?: GeoCoord;
}

export default function MapSearch({ onSelect, proximity }: MapSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    query,
    setQuery,
    results,
    suggestions,
    loading,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    selectResult,
    handleKeyDown,
    clearSearch,
  } = useMapSearch({ proximity, onResultSelect: onSelect });

  // Close on outside click
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [setIsOpen]);

  const allItems = [...suggestions, ...results];
  const hasResults = allItems.length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="flex items-center px-3">
          <MagnifyingGlassIcon
            className={`w-5 h-5 shrink-0 ${
              loading ? 'text-blue-500 animate-pulse' : 'text-slate-400'
            }`}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
            placeholder="Search fields, trees, farmers, places..."
            className="w-full px-3 py-3 text-sm bg-transparent focus:outline-none"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="p-1 rounded-md hover:bg-slate-100 text-slate-400"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Results dropdown */}
        {isOpen && hasResults && (
          <div className="border-t border-slate-100 max-h-72 overflow-y-auto">
            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Suggestions
                </div>
                {suggestions.map((s, i) => (
                  <button
                    key={`sug-${s.id}`}
                    onClick={() => selectResult(s)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-50 transition-colors ${
                      selectedIndex === i
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <div className="font-medium truncate">{s.label}</div>
                    {s.secondaryLabel && (
                      <div className="text-xs text-slate-400 truncate">
                        {s.secondaryLabel}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Entity results */}
            {results.length > 0 && (
              <div>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Results
                </div>
                {results.map((r, i) => {
                  const idx = suggestions.length + i;
                  return (
                    <button
                      key={`res-${r.entityType}-${r.entityId ?? idx}`}
                      onClick={() => selectResult(r)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={`w-full text-left px-4 py-2.5 text-sm border-b border-slate-50 transition-colors ${
                        selectedIndex === idx
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 capitalize">
                          {r.entityType}
                        </span>
                        <span className="font-medium truncate">{r.label}</span>
                      </div>
                      {r.secondaryLabel && (
                        <div className="text-xs text-slate-400 truncate mt-0.5">
                          {r.secondaryLabel}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {isOpen && query.length >= 2 && !hasResults && !loading && (
          <div className="px-4 py-6 text-center text-sm text-slate-400">
            No results for "{query}"
          </div>
        )}
      </div>
    </div>
  );
}
